'use client';

import React, { useCallback, useEffect } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

interface UseScopedOrgMemberFiltersArgs<TFilters extends object> {
  tableFilters: Partial<TFilters>;
  setTableFilters: React.Dispatch<React.SetStateAction<Partial<TFilters>>>;
  lockedFilters?: Partial<TFilters>;
  userFilterKey: Extract<keyof TFilters, string>;
  showDefaultToolbarSelectors?: boolean;
}

export function useScopedOrgMemberFilters<TFilters extends object>({
  tableFilters,
  setTableFilters,
  lockedFilters,
  userFilterKey,
  showDefaultToolbarSelectors = true,
}: UseScopedOrgMemberFiltersArgs<TFilters>) {
  const { kernel } = useKernel();
  const userRegistry = useKernelStore((state) => state.inventory.User.registry);
  const orgRegistry = useKernelStore((state) => state.inventory.Organisation.registry);
  const userContext = useKernelStore((state) => state.userContext);
  const tableFilterRecord = tableFilters as Record<string, unknown>;
  const lockedFilterRecord = (lockedFilters ?? {}) as Record<string, unknown>;

  const currentOrg = userContext.organisationId ? orgRegistry[userContext.organisationId] : null;
  const currentOrgId = currentOrg?.id;
  const lockedOrganisationId = lockedFilterRecord.organisationId as string | undefined;
  const activeOrgId =
    (tableFilterRecord.organisationId as string | undefined) ||
    (!userContext.isFaterAdmin ? currentOrgId || null : null);
  const isOrganisationLocked = lockedOrganisationId !== undefined;
  const isUserLocked = lockedFilterRecord[userFilterKey] !== undefined;
  const selectedUserId = (lockedFilterRecord[userFilterKey] || tableFilterRecord[userFilterKey]) as string | undefined;
  const selectedUser = selectedUserId ? userRegistry[selectedUserId] : null;
  const selectedOrgId =
    isOrganisationLocked
      ? lockedOrganisationId
      : (userContext.isFaterAdmin ? (tableFilterRecord.organisationId as string | undefined) : currentOrgId);
  const hasDefaultToolbarSelectors =
    showDefaultToolbarSelectors &&
    ((userContext.isFaterAdmin && !isOrganisationLocked) || !isUserLocked);

  useEffect(() => {
    if (isOrganisationLocked) return;
    if (userContext.isFaterAdmin) return;
    if (!currentOrgId) return;
    if (tableFilterRecord.organisationId === currentOrgId) return;

    setTableFilters((prev) => ({ ...prev, organisationId: currentOrgId } as Partial<TFilters>));
  }, [
    currentOrgId,
    isOrganisationLocked,
    setTableFilters,
    tableFilterRecord.organisationId,
    userContext.isFaterAdmin,
  ]);

  const handleOrgSelect = useCallback((orgId: string | null) => {
    if (isOrganisationLocked) return;
    setTableFilters((prev) => ({
      ...prev,
      organisationId: orgId ?? undefined,
      [userFilterKey]: undefined,
    } as Partial<TFilters>));
    kernel.current?.closeModal();
  }, [isOrganisationLocked, kernel, setTableFilters, userFilterKey]);

  const handleUserSelect = useCallback((user: { id?: string } | null) => {
    if (isUserLocked) return;
    setTableFilters((prev) => ({
      ...prev,
      [userFilterKey]: user?.id,
    } as Partial<TFilters>));
    kernel.current?.closeModal();
  }, [isUserLocked, kernel, setTableFilters, userFilterKey]);

  return {
    selectedOrgId: selectedOrgId ?? null,
    activeOrgId,
    selectedUser,
    hasDefaultToolbarSelectors,
    showOrganisationSelector: userContext.isFaterAdmin && !isOrganisationLocked,
    showUserSelector: !isUserLocked,
    handleOrgSelect,
    handleUserSelect,
  };
}
